import Dexie from 'dexie';
import { db, type OpLog } from './dexie';
import { cryptoService, type EncryptedPayload } from './crypto.service';

/**
 * Serviço Op-Log - Operation Log
 * AIDEV-NOTE: Todas as operações de escrita passam por aqui
 * Garante consistência e permite sync offline
 */

// AIDEV-NOTE: fotografia do op_log consumida pela superficie de sync na AppBar
export interface ContagemOpLog {
  pending: number;
  syncing: number;
  failed: number;
  /** Mensagem da falha mais recente — a UI mostra o motivo em vez de so um botao cego */
  ultimoErro: string | null;
}

export interface CreateOpLogInput {
  operation_type: string;
  entity_type: string;
  entity_id: string | number;
  payload: Record<string, unknown>;
  priority?: number;
}

export class OpLogService {
  /**
   * Registra nova operação no Op-Log
   * AIDEV-NOTE: Chamado atomicamente com a operação principal
   */
  async log(input: CreateOpLogInput): Promise<string> {
    const id = crypto.randomUUID();

    // AIDEV-NOTE: encrypt e promise estrangeira ao Dexie — sem waitFor a transacao commitaria prematuramente
    // (Dexie 3.2.4: aguardar promise nao-Dexie dentro de transaction() faz commit antecipado e nao-atomico)
    const encrypted_payload = Dexie.currentTransaction
      ? await Dexie.waitFor(cryptoService.encrypt(input.payload))
      : await cryptoService.encrypt(input.payload);

    const opLog: OpLog = {
      id,
      operation_type: input.operation_type,
      status: 'pending',
      entity_type: input.entity_type,
      entity_id: input.entity_id,
      encrypted_payload,
      retry_count: 0,
      created_at: Date.now(),
      priority: input.priority ?? 5
    };

    await db.op_log.add(opLog);

    return id;
  }

  /**
   * Busca operações pendentes de sync
   * AIDEV-NOTE: priority desc, empate por created_at asc — ordem causal (CREATE antes de FINALIZAR)
   */
  async getPendingOperations(limit: number = 50): Promise<OpLog[]> {
    const ops = await db.op_log.where('status').equals('pending').toArray();
    ops.sort((a, b) => b.priority - a.priority || a.created_at - b.created_at);
    return ops.slice(0, limit);
  }

  /**
   * Marca operação como em sincronização
   * AIDEV-NOTE: retorna linhas afetadas (0 se a op foi cancelada/deletada entre o fetch do
   * batch e o processamento) — usado como claim-check pelo drain em sync.service.ts
   */
  async markAsSyncing(id: string): Promise<number> {
    return await db.op_log.update(id, {
      status: 'syncing',
      updated_at: Date.now()
    });
  }

  /**
   * Devolve para 'pending' operacoes travadas em 'syncing'
   * AIDEV-NOTE: markAsSyncing grava 'syncing' antes do request; se o app e fechado (ou o SO mata a
   * aba) com o request em voo, nenhum markAsSynced/markAsFailed chega e a op fica invisivel para
   * getPendingOperations — que so olha 'pending' — para sempre. Na pratica: pedido perdido.
   * O limiar e folgado em relacao ao timeout de 30s do axios para nao competir com um request
   * legitimo em voo (inclusive de outra aba do mesmo vendedor).
   */
  async recuperarOpsTravadas(maxIdleMs: number = 5 * 60_000): Promise<number> {
    const limite = Date.now() - maxIdleMs;
    const travadas = await db.op_log.where('status').equals('syncing').toArray();

    let recuperadas = 0;
    for (const op of travadas) {
      const ultimaAtividade = op.updated_at ?? op.created_at;
      if (ultimaAtividade > limite) continue;

      await db.op_log.update(op.id, {
        status: 'pending',
        last_error: 'Sincronizacao interrompida (app fechado durante o envio) — reenfileirada'
      });
      recuperadas++;
    }

    return recuperadas;
  }

  /**
   * Marca operação como sincronizada
   */
  async markAsSynced(id: string): Promise<void> {
    await db.op_log.update(id, {
      status: 'synced',
      synced_at: Date.now()
    });
  }

  /**
   * Marca operação como falha
   */
  async markAsFailed(id: string, error: string): Promise<void> {
    const op = await db.op_log.get(id);

    if (!op) return;

    const retry_count = (op.retry_count ?? 0) + 1;

    // Após 3 tentativas, marca como failed permanente
    if (retry_count >= 3) {
      await db.op_log.update(id, {
        status: 'failed',
        retry_count,
        last_error: error
      });
    } else {
      // Volta para pending com backoff
      await db.op_log.update(id, {
        status: 'pending',
        retry_count,
        last_error: error
      });
    }
  }

  /**
   * Mescla campos no payload de uma operacao ainda nao sincronizada
   * AIDEV-NOTE: O rascunho criado offline entra no op_log com `itens: []` — o wizard cria o pedido
   * logo apos escolher o cliente, antes de existir qualquer item. Sem esta mesclagem o drain
   * criava um pedido VAZIO no servidor e marcava synced, perdendo tudo que foi digitado depois.
   * AIDEV-SECURITY: o payload e re-cifrado (AES-GCM) antes de voltar ao IndexedDB — em nenhum
   * momento ele fica em claro no banco local.
   * @returns true se alguma op foi atualizada
   */
  async mesclarPayloadPendente(
    entityType: string,
    entityId: string | number,
    operationType: string,
    patch: Record<string, unknown>
  ): Promise<boolean> {
    // AIDEV-NOTE: op_log NAO tem indice [entity_type+entity_id] — usa indice entity_type + filtro JS
    const ops = await db.op_log
      .where('entity_type')
      .equals(entityType)
      .filter(
        (op) =>
          op.entity_id === entityId &&
          op.operation_type === operationType &&
          // 'syncing' fica de fora: request em voo, reescrever o payload geraria divergencia
          (op.status === 'pending' || op.status === 'failed')
      )
      .toArray();

    if (ops.length === 0) return false;

    for (const op of ops) {
      const payloadAtual = await cryptoService.decrypt(op.encrypted_payload);
      const encrypted_payload = await cryptoService.encrypt({ ...payloadAtual, ...patch });
      await db.op_log.update(op.id, { encrypted_payload, updated_at: Date.now() });
    }

    return true;
  }

  /**
   * Descriptografa payload
   */
  async decrypt(encryptedPayload: EncryptedPayload): Promise<Record<string, unknown>> {
    return await cryptoService.decrypt(encryptedPayload);
  }

  /**
   * Limpa operações sincronizadas antigas
   */
  async cleanupSyncedOperations(maxAgeDays: number = 7): Promise<number> {
    const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;

    const oldOps = await db.op_log
      .where('status')
      .equals('synced')
      .and((op) => !!op.synced_at && op.synced_at < cutoff)
      .toArray();

    if (oldOps.length > 0) {
      await db.op_log.bulkDelete(oldOps.map((op) => op.id));
    }

    return oldOps.length;
  }

  /**
   * Conta operações pendentes
   */
  async getPendingCount(): Promise<number> {
    return await db.op_log.where('status').equals('pending').count();
  }

  /**
   * Fotografia do que ainda não chegou ao servidor, por estado
   * AIDEV-NOTE: getPendingCount conta SO 'pending' — uma op que falhou 3x vira 'failed' e sumia
   * do contador em silencio; uma travada em 'syncing' idem. Era por isso que o vendedor nao tinha
   * como saber que um pedido nao subiu. Uma varredura so, para o useLiveQuery observar barato.
   */
  async contarPorStatus(): Promise<ContagemOpLog> {
    const ops = await db.op_log.where('status').anyOf(['pending', 'syncing', 'failed']).toArray();

    const falhas = ops.filter((op) => op.status === 'failed');
    // Falha mais recente primeiro — e a mensagem que a UI mostra como motivo
    const maisRecente = falhas.sort(
      (a, b) => (b.updated_at ?? b.created_at) - (a.updated_at ?? a.created_at)
    )[0];

    return {
      pending: ops.filter((op) => op.status === 'pending').length,
      syncing: ops.filter((op) => op.status === 'syncing').length,
      failed: falhas.length,
      ultimoErro: maisRecente?.last_error ?? null
    };
  }

  /**
   * Devolve para a fila as operações que esgotaram as 3 tentativas
   * AIDEV-SECURITY: so mexe em 'failed'. Reenfileirar uma op 'synced' reenviaria ao servidor algo
   * que ja chegou (pedido duplicado no ERP); uma 'syncing' tem request em voo.
   * @returns quantas voltaram para a fila
   */
  async reenfileirarFalhas(): Promise<number> {
    const falhas = await db.op_log.where('status').equals('failed').toArray();

    for (const op of falhas) {
      await db.op_log.update(op.id, {
        status: 'pending',
        retry_count: 0,
        updated_at: Date.now()
      });
    }

    return falhas.length;
  }

  /**
   * Busca operações por entidade
   */
  async getByEntity(entityType: string, entityId: string | number): Promise<OpLog[]> {
    return await db.op_log
      .where('[entity_type+entity_id]')
      .equals([entityType, entityId])
      .toArray();
  }

  /**
   * Cancela operação pendente
   */
  async cancel(id: string): Promise<void> {
    await db.op_log.delete(id);
  }
}

export const opLogService = new OpLogService();
